import {type CSSProperties, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import './PurchaseSuccessModal.css'

interface Props {
  onPrimary: () => void
  onClose: () => void
  // Optional copy overrides. When omitted, the post-purchase copy is used, so the
  // modal is reused as-is for other celebratory moments (e.g. promo-code redeem).
  eyebrow?: string
  title?: string
  body?: string
  primaryLabel?: string
  secondaryLabel?: string
}

const CONFETTI_COUNT = 80

const CONFETTI_COLORS = ['#7c5cff', '#a78bfa', '#c4b5fd', '#ff7ab8', '#ffd166', '#86efac', '#7dd3fc', '#fb923c']

const CONFETTI_SHAPES = ['square', 'rect', 'circle'] as const

interface ConfettiPiece {
  color: string
  shape: (typeof CONFETTI_SHAPES)[number]
  leftPct: number
  size: number
  rotate: number
  drift: number
  duration: number
  delay: number
}

const buildConfetti = (): ConfettiPiece[] =>
  Array.from({length: CONFETTI_COUNT}, () => ({
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]!,
    shape: CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)]!,
    leftPct: Math.random() * 100,
    size: 6 + Math.random() * 8,
    rotate: Math.random() * 360,
    drift: (Math.random() - 0.5) * 200,
    duration: 2.8 + Math.random() * 2.2,
    delay: Math.random() * 0.6,
  }))

const PurchaseSuccessModal = ({onPrimary, onClose, eyebrow, title, body, primaryLabel, secondaryLabel}: Props) => {
  const {t} = useTranslation()
  const confetti = useMemo(() => buildConfetti(), [])

  const eyebrowText = eyebrow ?? t('account.purchaseSuccess.eyebrow')
  const titleText = title ?? t('account.purchaseSuccess.title')
  const bodyText = body ?? t('account.purchaseSuccess.body')
  const primaryText = primaryLabel ?? t('account.purchaseSuccess.ctaPrimary')
  const secondaryText = secondaryLabel ?? t('account.purchaseSuccess.ctaSecondary')

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  return (
    <div className="ps-backdrop" onClick={onClose} role="presentation">
      <div className="ps-confetti" aria-hidden="true">
        {confetti.map((c, i) => (
          <span
            key={i}
            className={`ps-piece ps-piece-${c.shape}`}
            style={
              {
                left: `${c.leftPct}%`,
                width: c.shape === 'rect' ? c.size : c.size,
                height: c.shape === 'rect' ? c.size * 1.8 : c.size,
                background: c.color,
                animationDuration: `${c.duration}s`,
                animationDelay: `${c.delay}s`,
                '--ps-rot-start': `${c.rotate}deg`,
                '--ps-rot-end': `${c.rotate + 540}deg`,
                '--ps-drift': `${c.drift}px`,
              } as CSSProperties
            }
          />
        ))}
      </div>

      <div
        className="ps-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ps-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ps-close" onClick={onClose} aria-label={secondaryText}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="ps-check-wrap">
          <span className="ps-check-ring" />
          <span className="ps-check-ring ps-check-ring-2" />
          <span className="ps-check-disc">
            <svg
              width="34"
              height="34"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12l5 5L20 7" />
            </svg>
          </span>
        </div>

        <div className="ps-eyebrow">{eyebrowText}</div>
        <h3 id="ps-modal-title" className="ps-title">
          {titleText}
        </h3>
        <p className="ps-body">{bodyText}</p>

        <div className="ps-actions">
          <button type="button" className="ps-btn ps-btn-ghost" onClick={onClose}>
            {secondaryText}
          </button>
          <button type="button" className="ps-btn ps-btn-primary" onClick={onPrimary}>
            {primaryText}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PurchaseSuccessModal
